// src/components/ui/Alert.tsx
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, CheckCircle, Info, XCircle } from 'lucide-react';

interface AlertProps {
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  onClose?: () => void;
  autoClose?: boolean;
  duration?: number;
}

export const Alert: React.FC<AlertProps> = ({ 
  type, 
  message, 
  onClose, 
  autoClose = true,
  duration = 5000 
}) => {
  React.useEffect(() => {
    if (autoClose && onClose) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [autoClose, duration, onClose]);

  const icons = {
    success: <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400" />,
    error: <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />,
    info: <Info className="w-5 h-5 text-blue-500 dark:text-blue-400" />,
    warning: <XCircle className="w-5 h-5 text-yellow-500 dark:text-yellow-400" />,
  };

  const colors = {
    success: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/50 text-green-600 dark:text-green-400',
    error: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400',
    info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/50 text-blue-600 dark:text-blue-400',
    warning: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800/50 text-yellow-600 dark:text-yellow-400',
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={`p-3 rounded-xl border flex items-center gap-2 ${colors[type]}`}
      >
        {icons[type]}
        <p className="text-sm flex-1">{message}</p>
        {onClose && (
          <button onClick={onClose} className="hover:opacity-70 transition">
            <XCircle className="w-4 h-4" />
          </button>
        )}
      </motion.div>
    </AnimatePresence>
  );
};